<template>
    <!-- .pagebuilder-content is the print target: print.css hides everything else (see assets/css/print.css) -->
    <div
        v-if="
            data.type === PageTypes.Navigation ||
            data.type === PageTypes.Blog ||
            data.type === PageTypes.ManufacturerDetail
        "
        class="pagebuilder-content">
        <Pagebuilder
            :type="schemaType"
            :id="data.id"
            :shopwareId="data.id"
            :blog="data.type === PageTypes.Blog" />
    </div>

    <BlockProductListing
        v-if="data.type == PageTypes.ManufacturerCategory"
        :shopwareId="data.id"
        :category="data.category"
        :title="manufacturerCategoryTitle"
        :type="data.type"
        :typePage="PageTypes.ManufacturerDetail" />

    <TempContent :data />

    <ShopwareProductDetailLayout
        v-if="data.type === PageTypes.ProductDetail"
        :product="data.product"
        :colorVariants="data?.colorVariants"
        :recommendations="data?.recommendations"
        :brandRecommendations="data?.brandRecommendations" />

    <LookLayout v-if="data.type === PageTypes.ShopTheLook" :look="data.look" />
    <!-- NOTE: fetchRelatedBlogs is async, so :blogs receives a pending Promise, not resolved data -->
    <BlogRelatedBlogs v-if="data.type === PageTypes.Blog" :blogs="fetchRelatedBlogs(data.id)" />

    <!-- Print button - only shows print button for terms and conditions -->
    <div v-if="isTermsAndConditionsPage" class="no-print boxed-content my-8 flex max-w-4xl justify-center px-4">
        <button @click="printPage" class="btn btn-primary flex items-center gap-2">
            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            Print Algemene Voorwaarden
        </button>
    </div>
</template>

<script setup lang="ts">
    import { type PageData, PageTypes, SanitySchemaTypes } from '#shared/resolve-page';
    import TempContent from '~/components/TempContent.vue';

    definePageMeta({
        key: route => route.path,
    });

    const { isTermsAndConditionsPage, printPage } = usePrintPage();
    const pageDataStore = usePageDataStore();
    const gtm = useGtm();
    const route = useRoute();
    const slug = route.params.slug;
    // Catch-all route: slug is a string[] of path segments, rejoin to the full path
    const slugJoined = Array.isArray(slug) ? slug.join('/') : slug;
    const data: Ref<PageData | null> = ref(((await useResolvePage(slugJoined)) as PageData) ?? null);

    // Use useState for manufacturer — provide() after await silently fails on client-side navigation
    const manufacturerState = useState<string | undefined>(`manufacturer-${route.path}`, () => undefined);
    const manufacturerNameState = useState<string | undefined>(`manufacturerName-${route.path}`, () => undefined);
    const topContentState = useState<string | undefined>(`topContent-${route.path}`, () => undefined);
    if (data.value?.type === PageTypes.ManufacturerDetail || data.value?.type === PageTypes.ManufacturerCategory) {
        manufacturerState.value = data.value?.manufacturer?.id;
        manufacturerNameState.value = data.value?.manufacturer?.translated?.name ?? data.value?.manufacturer?.name;
        topContentState.value = data.value?.seo?.topContent ?? undefined;
    } else {
        manufacturerState.value = undefined;
        manufacturerNameState.value = undefined;
        topContentState.value = undefined;
    }
    // Reassigned below to map page type → Sanity schema type for Pagebuilder
    const schemaType = ref(data.value?.type);
    const lastBreadCrumbItem = ref(
        data.value?.breadcrumb?.length > 0 ? data.value?.breadcrumb[data.value?.breadcrumb?.length - 1] : null,
    );

    // For manufacturer category pages, construct the title from the manufacturer name and the
    // variant/category name. For sale/new variants, the backend returns the root "Home" category,
    // so we use the last breadcrumb item or the variant name as a fallback.
    const manufacturerCategoryTitle = computed(() => {
        const manufacturerName = data.value?.manufacturer?.translated?.name ?? '';
        const categoryName = data.value?.category?.translated?.name;
        const variant = data.value?.variant;

        // If the category is the root "Home" category (sale/new variants), prefer breadcrumb or variant name
        if (categoryName === 'Home' && (variant === 'sale' || variant === 'nieuw')) {
            const fallbackName = lastBreadCrumbItem.value?.name ?? variant ?? '';
            return `${manufacturerName} ${fallbackName}`.trim();
        }

        return `${manufacturerName} ${categoryName ?? lastBreadCrumbItem.value?.name ?? ''}`.trim();
    });

    if (data.value === null || data.value === PageTypes.NotFound || data.value === PageTypes.Error) {
        throw createError({
            statusCode: 404,
            statusMessage: 'Pagina niet gevonden',
            fatal: true,
        });
    }

    // NOTE: navigateTo is not returned, so setup below keeps running for a redirecting page
    if (data.value?.type === PageTypes.Redirect) {
        navigateTo(data.value?.redirect, { redirectCode: 301 });
    }

    pageDataStore.setData(data.value);
    provide('breadcrumbs', data.value?.breadcrumb ?? []);
    provide('categoryVariantUrls', data.value?.variantUrls ?? []);

    useSetHead(data.value?.seo, data.value?.type ?? '', data.value?.product);
    onMounted(() => {
        triggerEvent();
    });

    // Map page types to their corresponding Sanity schema type for the Pagebuilder
    if (schemaType.value === PageTypes.Navigation) {
        schemaType.value = SanitySchemaTypes.Page;
    } else if (
        schemaType.value === PageTypes.ManufacturerDetail ||
        schemaType.value === PageTypes.ManufacturerCategory
    ) {
        schemaType.value = SanitySchemaTypes.Manufacturer;
    }

    async function fetchRelatedBlogs(currentBlogId: string) {
        const { data } = await useFetch(`/api/blogs/relatedBlogs/${currentBlogId}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                blogId: currentBlogId,
            }),
        });
        if (data.value) {
            return data.value;
        }
    }

    function triggerEvent() {
        gtm?.trackView(slugJoined, route.fullPath);
    }
</script>

<style scoped>
    /* Fade in the entire page when isPageLoaded is true */
    .site-container {
        opacity: 0;
        transition: opacity 0.2s ease;
        height: 100vh;
    }

    .site-container.is-loaded {
        opacity: 1;
        height: auto;
    }

    .fade-enter-active,
    .fade-leave-active {
        transition: opacity 0.5s ease;
    }

    .fade-enter, .fade-leave-to /* .fade-leave-active in <2.1.8 */ {
        opacity: 0;
    }
</style>
